import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Instagram, Twitter, Linkedin, Youtube } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  const footerLinks = {
    navigation: [
      { name: t('nav.services'), path: '/services' },
      { name: t('nav.packages'), path: '/packages' },
      { name: t('nav.about'), path: '/about' },
      { name: t('nav.contact'), path: '/contact' },
    ],
    services: [
      { name: 'Brand Authority', path: '/services#brand-authority' },
      { name: 'Lead Capture Funnels', path: '/services#lead-capture' },
      { name: 'CRM + AI Automation', path: '/services#crm-automation' },
      { name: 'AI Sales Desk', path: '/services#ai-sales-desk' },
      { name: 'ROI Dashboards', path: '/services#roi-dashboards' },
    ],
    social: [
      { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/bankkimedia' },
      { name: 'Twitter', icon: Twitter, url: 'https://twitter.com/bankkimedia' },
      { name: 'LinkedIn', icon: Linkedin, url: 'https://linkedin.com/company/bankkimedia' },
      { name: 'YouTube', icon: Youtube, url: 'https://youtube.com/@bankkimedia' },
    ],
  };

  return (
    <footer data-testid="footer" className="bg-black text-white">
      {/* Big CTA Section */}
      <div className="container-custom py-24 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-gray-400 text-sm uppercase tracking-widest mb-6">
            {t('footer.cta')}
          </p>
          <Link
            to="/contact"
            data-testid="footer-cta"
            className="group inline-flex items-center gap-4"
          >
            <motion.span
              whileHover={{ x: -10 }}
              className="text-5xl md:text-7xl lg:text-8xl font-bold font-['Syne'] tracking-tight hover:text-gray-300 transition-colors"
            >
              {t('footer.getInTouch')}
            </motion.span>
            <motion.div
              whileHover={{ x: 10, y: -10 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <ArrowUpRight className="w-12 h-12 md:w-16 md:h-16" />
            </motion.div>
          </Link>
        </motion.div>

        {/* Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 py-16 border-t border-white/10">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Link to="/" className="inline-block mb-6">
              <span className="text-2xl font-bold font-['Syne']">Bankki</span>
              <span className="text-2xl font-light font-['Syne'] text-gray-400 ml-1">Media</span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
          </motion.div>

          {/* Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h4 className="text-sm font-semibold uppercase tracking-widest mb-6">
              {t('footer.navigate')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.navigation.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-white transition-colors text-sm inline-block"
                  >
                    <motion.span whileHover={{ x: 4 }} className="inline-block">
                      {link.name}
                    </motion.span>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4 className="text-sm font-semibold uppercase tracking-widest mb-6">
              {t('footer.servicesLabel')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-white transition-colors text-sm inline-block"
                  >
                    <motion.span whileHover={{ x: 4 }} className="inline-block">
                      {link.name}
                    </motion.span>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Social */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h4 className="text-sm font-semibold uppercase tracking-widest mb-6">
              {t('footer.connect')}
            </h4>
            <div className="flex gap-3">
              {footerLinks.social.map((social) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-${social.name.toLowerCase()}`}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-all"
                    aria-label={social.name}
                  >
                    <Icon className="w-4 h-4" />
                  </motion.a>
                );
              })}
            </div>
            <div className="mt-8">
              <p className="text-gray-400 text-sm">contact@bankkimedia.com</p>
              <p className="text-gray-400 text-sm mt-1">+1 (323) 577-7420</p>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-white/10 gap-4">
          <p className="text-gray-500 text-sm">
            © {currentYear} Bankki Media. {t('footer.rights')}
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-gray-500 hover:text-white text-sm transition-colors">
              {t('footer.privacy')}
            </Link>
            <Link to="/terms" className="text-gray-500 hover:text-white text-sm transition-colors">
              {t('footer.terms')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
